import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMaintenanceMode } from '../contexts/MaintenanceContext';
import { useAuth } from '../contexts/AuthContext';
import MaintenancePage from '../pages/MaintenancePage';
import LoadingFallback from './LoadingFallback';

/**
 * MaintenanceGuard component that checks if maintenance mode is enabled
 * and shows the maintenance page for non-admin users.
 * 
 * Bypasses maintenance check for:
 * - Admin routes (/admin/*)
 * - Login routes (/login, /admin-login)
 * - Verification routes (/verify-email, /email-verification/*)
 * - Password reset routes (/forgot-password, /reset-password/*)
 * - Admin users
 */
const MaintenanceGuard = ({ children }) => {
  const { isMaintenanceMode, loading } = useMaintenanceMode();
  const { isAdmin, isAuthenticated } = useAuth();
  const location = useLocation();

  // Show loading state while checking maintenance status
  if (loading) {
    return <LoadingFallback message="Loading..." />;
  }

  // Bypass paths that should always be accessible
  const bypassPaths = [
    '/login',
    '/admin-login',
    '/admin',
    '/verify-email',
    '/email-verification',
    '/forgot-password',
    '/reset-password',
    '/terms',
    '/privacy-policy',
    '/return-policy'
  ];

  // Check if current path should bypass maintenance check
  const shouldBypass = bypassPaths.some(path => 
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // If maintenance mode is enabled
  if (isMaintenanceMode) {
    // Always allow admin routes
    if (location.pathname.startsWith('/admin')) {
      return children;
    }

    // Allow bypassed paths
    if (shouldBypass) {
      return children;
    }

    // Allow authenticated admins
    if (isAuthenticated && isAdmin()) {
      return children;
    }

    // Show maintenance page for everyone else
    return <MaintenancePage />;
  }

  // Not in maintenance mode, render children normally
  return children;
};

export default MaintenanceGuard;

